import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MessageCircle, Send, User, ArrowLeft, Loader, Home, Paperclip, Download } from 'lucide-react';
import { messageService, adminService } from '../services/api';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';

interface Conversation {
  user: {
    id: number;
    nom: string;
    prenom: string;
    role: string;
  } | null;
  apartment: {
    id: number;
    titre: string;
  } | null;
  last_message: {
    id: number;
    contenu: string;
    created_at: string;
    is_mine: boolean;
  };
  unread_count: number;
}

interface Message {
  id: number;
  sender_id: number;
  receiver_id: number;
  contenu: string;
  sujet?: string;
  created_at: string;
  is_read: boolean;
  attachment_path?: string;
  attachment_name?: string;
  sender?: { id: number; nom: string; prenom: string };
  receiver?: { id: number; nom: string; prenom: string };
  apartment?: { id: number; titre: string };
}

interface Gerant {
  id: number;
  nom: string;
  prenom: string;
  email: string;
}

const MessagesPage: React.FC = () => {
  const { user } = useAuthStore();
  const [searchParams] = useSearchParams();
  const isLocataire = user?.role === 'ROLE_LOCATAIRE';
  
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<number | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [otherUser, setOtherUser] = useState<{ id: number; nom: string; prenom: string; role: string } | null>(null);
  
  // Pour nouveau message (locataire)
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [gerants, setGerants] = useState<Gerant[]>([]);
  const [selectedGerant, setSelectedGerant] = useState<number | null>(null);
  const [newSubject, setNewSubject] = useState('');
  const [newContent, setNewContent] = useState('');
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Vue mobile: liste ou conversation
  const [mobileView, setMobileView] = useState<'list' | 'chat'>('list');
  
  // Vérifier si on doit ouvrir une conversation spécifique (depuis notification)
  const openUserId = searchParams.get('user');

  useEffect(() => {
    fetchConversations();
    if (isLocataire) {
      fetchGerants();
    }
  }, [isLocataire]);
  
  // Ouvrir automatiquement une conversation si user_id est dans l'URL
  useEffect(() => {
    if (openUserId && !loading) {
      const userId = parseInt(openUserId);
      if (!isNaN(userId)) {
        openConversation(userId);
        setMobileView('chat');
      }
    }
  }, [openUserId, loading]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      const response = await messageService.getConversations();
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGerants = async () => {
    try {
      // Utiliser une route pour récupérer les gérants disponibles
      const response = await adminService.getLocataires();
      // Filtrer pour ne garder que les gérants si besoin
      // Pour l'instant on va utiliser les conversations existantes ou permettre de contacter via appartement
    } catch (error) {
      console.error(error);
    }
  };

  const openConversation = async (userId: number) => {
    setSelectedConversation(userId);
    setLoading(true);
    try {
      const response = await messageService.getConversation(userId);
      setMessages(response.data.messages || []);
      setOtherUser(response.data.other_user);
      // Refresh conversations to update unread count
      fetchConversations();
    } catch (error) {
      toast.error('Erreur lors du chargement de la conversation');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;
    
    setSending(true);
    try {
      await messageService.send({
        receiver_id: selectedConversation,
        contenu: newMessage.trim()
      });
      setNewMessage('');
      // Recharger les messages
      openConversation(selectedConversation);
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const handleSendNewMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newContent.trim() || !selectedGerant) return;
    
    setSending(true);
    try {
      await messageService.send({
        receiver_id: selectedGerant,
        contenu: newContent.trim(),
        sujet: newSubject.trim() || undefined
      });
      toast.success('Message envoyé !');
      setShowNewMessage(false);
      setNewContent('');
      setNewSubject('');
      setSelectedGerant(null);
      fetchConversations();
      // Ouvrir la conversation
      openConversation(selectedGerant);
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Hier ' + date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) + 
             ' ' + date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    }
  };

  const handleDownloadAttachment = async (messageId: number, fileName: string) => {
    try {
      const response = await messageService.downloadAttachment(messageId);
      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName || 'facture.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Téléchargement démarré');
    } catch (error) {
      console.error('Erreur téléchargement:', error);
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleSelectConversation = (userId: number) => {
    openConversation(userId);
    setMobileView('chat');
  };

  const handleBackToList = () => {
    setMobileView('list');
    setSelectedConversation(null);
  };

  if (loading && conversations.length === 0) {
    return (
      <div className="flex justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-200px)] flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
          <MessageCircle className="w-6 h-6" />
          Messages
        </h1>
        {isLocataire && conversations.length > 0 && (
          <button
            onClick={() => setShowNewMessage(true)}
            className="btn btn-primary btn-sm"
          >
            Nouveau message
          </button>
        )}
      </div>

      {/* Modal nouveau message */}
      {showNewMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="font-semibold">Nouveau message</h2>
              <button onClick={() => setShowNewMessage(false)} className="text-neutral-500 hover:text-neutral-700">
                ×
              </button>
            </div>
            <form onSubmit={handleSendNewMessage} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Destinataire</label>
                <select
                  value={selectedGerant || ''}
                  onChange={(e) => setSelectedGerant(Number(e.target.value))}
                  className="input w-full"
                  required
                >
                  <option value="">Sélectionner un gérant</option>
                  {conversations.map(conv => conv.user && (
                    <option key={conv.user.id} value={conv.user.id}>
                      {conv.user.prenom} {conv.user.nom}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-neutral-500 mt-1">
                  Vous pouvez contacter les gérants avec qui vous avez déjà échangé
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Sujet (optionnel)</label>
                <input
                  type="text"
                  value={newSubject}
                  onChange={(e) => setNewSubject(e.target.value)}
                  className="input w-full"
                  placeholder="Ex: Question sur ma réservation"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Message</label>
                <textarea
                  value={newContent}
                  onChange={(e) => setNewContent(e.target.value)}
                  className="input w-full min-h-[120px]"
                  placeholder="Votre message..."
                  required
                />
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowNewMessage(false)} className="btn btn-outline flex-1">
                  Annuler
                </button>
                <button type="submit" disabled={sending} className="btn btn-primary flex-1">
                  {sending ? <Loader className="w-4 h-4 animate-spin" /> : 'Envoyer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {conversations.length === 0 && !selectedConversation ? (
        <div className="card p-8 text-center flex-1 flex flex-col items-center justify-center">
          <MessageCircle className="w-16 h-16 text-neutral-300 mb-4" />
          <p className="text-neutral-500 mb-4">Aucune conversation pour le moment.</p>
          {isLocataire && (
            <p className="text-sm text-neutral-400">
              Vous pouvez contacter un gérant depuis la page de détail d'un appartement.
            </p>
          )}
        </div>
      ) : (
        <div className="card flex-1 flex overflow-hidden">
          {/* Liste des conversations */}
          <div className={`w-full md:w-1/3 border-r flex flex-col ${mobileView === 'chat' ? 'hidden md:flex' : 'flex'}`}>
            <div className="p-3 border-b bg-neutral-50">
              <h2 className="font-medium text-sm text-neutral-600">Conversations</h2>
            </div>
            <div className="flex-1 overflow-y-auto">
              {conversations.map((conv) => (
                <div
                  key={conv.user?.id || 'unknown'}
                  onClick={() => conv.user && handleSelectConversation(conv.user.id)}
                  className={`p-3 border-b cursor-pointer hover:bg-neutral-50 transition-colors ${
                    selectedConversation === conv.user?.id ? 'bg-primary-50 border-l-4 border-l-primary-500' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-primary-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="font-medium truncate">
                          {conv.user ? `${conv.user.prenom} ${conv.user.nom}` : 'Utilisateur'}
                        </span>
                        {conv.unread_count > 0 && (
                          <span className="bg-primary-500 text-white text-xs px-2 py-0.5 rounded-full">
                            {conv.unread_count}
                          </span>
                        )}
                      </div>
                      {conv.apartment && (
                        <div className="flex items-center gap-1 text-xs text-neutral-500">
                          <Home className="w-3 h-3" />
                          {conv.apartment.titre}
                        </div>
                      )}
                      <p className="text-sm text-neutral-500 truncate mt-1">
                        {conv.last_message.is_mine && <span className="text-neutral-400">Vous: </span>}
                        {conv.last_message.contenu}
                      </p>
                      <p className="text-xs text-neutral-400 mt-1">
                        {formatDate(conv.last_message.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Zone de conversation */}
          <div className={`flex-1 flex flex-col ${mobileView === 'list' ? 'hidden md:flex' : 'flex'}`}>
            {selectedConversation && otherUser ? (
              <>
                {/* Header */}
                <div className="p-3 border-b bg-neutral-50 flex items-center gap-3">
                  <button
                    onClick={handleBackToList}
                    className="md:hidden p-1 hover:bg-neutral-200 rounded"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                    <User className="w-4 h-4 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-medium">{otherUser.prenom} {otherUser.nom}</h3>
                    <p className="text-xs text-neutral-500">
                      {otherUser.role === 'ROLE_GERANT' ? 'Gérant' : 'Locataire'}
                    </p>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-neutral-50">
                  {messages.map((msg) => {
                    const isMine = msg.sender_id === user?.id;
                    return (
                      <div
                        key={msg.id}
                        className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[75%] rounded-2xl px-4 py-2 ${
                            isMine
                              ? 'bg-primary-500 text-white rounded-br-md'
                              : 'bg-white shadow-sm rounded-bl-md'
                          }`}
                        >
                          {msg.sujet && (
                            <p className={`text-xs font-medium mb-1 ${isMine ? 'text-primary-100' : 'text-neutral-500'}`}>
                              {msg.sujet}
                            </p>
                          )}
                          <p className="whitespace-pre-wrap">{msg.contenu}</p>
                          
                          {/* Pièce jointe */}
                          {msg.attachment_path && (
                            <button
                              onClick={() => handleDownloadAttachment(msg.id, msg.attachment_name || 'facture.pdf')}
                              className={`flex items-center gap-2 mt-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                                isMine 
                                  ? 'bg-primary-400 hover:bg-primary-300 text-white' 
                                  : 'bg-neutral-100 hover:bg-neutral-200 text-neutral-700'
                              }`}
                            >
                              <Paperclip className="w-4 h-4" />
                              <span className="truncate max-w-[150px]">
                                {msg.attachment_name || 'Pièce jointe'}
                              </span>
                              <Download className="w-4 h-4" />
                            </button>
                          )}
                          
                          <p className={`text-xs mt-1 ${isMine ? 'text-primary-200' : 'text-neutral-400'}`}>
                            {formatDate(msg.created_at)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <form onSubmit={handleSendMessage} className="p-3 border-t bg-white">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Écrire un message..."
                      className="input flex-1"
                      disabled={sending}
                    />
                    <button
                      type="submit"
                      disabled={sending || !newMessage.trim()}
                      className="btn btn-primary px-4"
                    >
                      {sending ? (
                        <Loader className="w-5 h-5 animate-spin" />
                      ) : (
                        <Send className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-neutral-400">
                <div className="text-center">
                  <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Sélectionnez une conversation</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MessagesPage;
